import { el, replaceChildren } from "../dom.ts";
import { formatMoney, formatMoneyParts } from "../format/money.ts";
import { prefersReducedMotion } from "../motion.ts";
import { icon } from "./icon.ts";

/**
 * Amount composition — the interaction Nura is actually for.
 *
 * PHASE_01_PRODUCTION_ARCHITECTURE.md §17: "The keypad emits digits with an
 * explicit decimal decision; locale strings are never parsed from a text field."
 *
 * This is implemented literally rather than approximated. Digits accumulate
 * straight into **minor units** as a `bigint`, so the decimal point is never
 * typed, never parsed and never ambiguous — it is the currency's ISO 4217
 * exponent, applied at render time only. Typing 1, 2, 3, 4 in a
 * two-minor-digit currency produces `1234n` minor units, and the separator a
 * person sees is whatever their locale uses.
 *
 * Consequences worth stating, because they are the reason for the design:
 *
 * - there is no text field to paste a locale string into;
 * - there is no code path where a money value is a `number`;
 * - a currency with a different exponent needs no special case.
 *
 * ## Scope
 *
 * §12 places `DRAFT_AMOUNT` outside the server graph: "[client-local]
 * DRAFT_AMOUNT (never a server state)". This component owns exactly that draft
 * and nothing after it. It performs no authorization, mints no identifier,
 * contacts no bank, and persists nothing.
 */

/**
 * Upper bound of the signed 64-bit representation §17 fixes for money.
 *
 * This is a **representation** bound taken from frozen architecture, not a
 * transaction limit. Limits, thresholds and tenant bounds belong to the bank
 * (`CLAUDE.md` § Product boundary), and none is invented here.
 */
const INT64_MAX = 9223372036854775807n;

/** Client-local `DRAFT_AMOUNT`: a digit accumulator over minor units. */
export class AmountDraft {
  #digits = "";

  /** Minor units entered so far. Zero when nothing has been typed. */
  get minorUnits(): bigint {
    return this.#digits === "" ? 0n : BigInt(this.#digits);
  }

  /** True while the draft is untouched, which the display renders as a prompt. */
  get isEmpty(): boolean {
    return this.#digits === "";
  }

  /**
   * Append one digit.
   *
   * Returns `false` when the digit is rejected — either it is not a digit, or
   * accepting it would put the value outside the §17 representation. Rejection
   * is silent to the model; the component decides how to surface it.
   */
  appendDigit(digit: string): boolean {
    if (digit.length !== 1 || digit < "0" || digit > "9") return false;
    // Leading zeros carry no value and would let the digit count run away.
    const next = this.#digits === "" && digit === "0" ? "" : `${this.#digits}${digit}`;
    if (next !== "" && BigInt(next) > INT64_MAX) return false;
    this.#digits = next;
    return true;
  }

  /** Remove the last digit. Returns `false` when there was nothing to remove. */
  deleteLast(): boolean {
    if (this.#digits === "") return false;
    this.#digits = this.#digits.slice(0, -1);
    return true;
  }

  /** Discard the draft. Returns `false` when it was already empty. */
  clear(): boolean {
    if (this.#digits === "") return false;
    this.#digits = "";
    return true;
  }
}

const KEYPAD_DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"] as const;

export interface AmountComposerOptions {
  /** Starting currency. Comes from the tenant's `defaultCurrency`. */
  readonly currency: string;
  readonly locale: string;
  /**
   * Currencies to offer, **in the order they should appear**.
   *
   * Supplied by tenant configuration, never hard-coded here. One entry renders
   * no selector at all — a single-currency institution should not be shown a
   * control with one option.
   */
  readonly currencies: readonly string[];
  /**
   * The primary action, rendered inside the composer.
   *
   * The composer owns the layout; the route owns what the button says and does.
   * Putting the action inside the same surface as the amount and the keypad is
   * what makes this one object rather than three stacked ones — and it is the
   * shape that compresses into a keyboard, where the action sits on the deck
   * with the keys.
   */
  readonly action?: Node;
  readonly onChange?: (minorUnits: bigint, currency: string) => void;
}

export interface AmountComposer {
  readonly root: HTMLElement;
  readonly minorUnits: bigint;
  readonly currency: string;
  /** Select a supported currency. Clears the amount; never converts it. */
  setCurrency(code: string): void;
  /** Detach the window-level key listener. Call when the view is torn down. */
  destroy(): void;
}

/**
 * Build the composer: value display, currency chooser, keypad.
 *
 * Physical-keyboard entry is bound at the window while the composer is on
 * screen, because on a desktop the keypad is visible but rarely the focused
 * element — requiring a click before typing would be a worse interface than the
 * one it imitates. Events are ignored when a modifier is held or when focus is
 * inside a text control, so browser shortcuts and any future form still work.
 */
export function amountComposer(options: AmountComposerOptions): AmountComposer {
  const draft = new AmountDraft();
  let currency = options.currency;

  const value = el("p", { class: "nura-amount__value" });
  const caption = el("p", { class: "nura-amount__caption" });
  const announcement = el("span", { class: "nura-sr-only" });

  const display = el(
    "div",
    {
      class: "nura-amount",
      attrs: { role: "status", "aria-live": "polite", "aria-atomic": "true" },
    },
    [value, announcement, caption],
  );

  /**
   * A single-frame acknowledgement that the value changed.
   *
   * The animation itself lives in CSS with the rest of the motion system —
   * durations and easings belong beside the motion tokens, not hard-coded in a
   * component. Re-triggering it alternates between two identically-defined
   * keyframes, which restarts the animation without a layout read or a frame
   * of latency. Skipped entirely under `prefers-reduced-motion`.
   */
  let pulsePhase = false;

  function pulse(): void {
    if (prefersReducedMotion()) return;
    pulsePhase = !pulsePhase;
    value.setAttribute("data-pulse", pulsePhase ? "a" : "b");
  }

  function render(): void {
    const parts = formatMoneyParts(draft.minorUnits, currency, options.locale);
    const symbol = el("span", { class: "nura-amount__symbol", text: parts.symbol });
    const digits = [
      el("span", { text: parts.major }),
      parts.minor === ""
        ? undefined
        : el("span", { class: "nura-amount__minor", text: parts.minor }),
    ];

    value.setAttribute("data-empty", draft.isEmpty ? "true" : "false");
    replaceChildren(value, parts.symbolLeads ? [symbol, ...digits] : [...digits, symbol]);
    announcement.textContent = formatMoney(draft.minorUnits, currency, options.locale);
  }

  function setCaption(message: string, tone: "muted" | "attention" | "danger"): void {
    caption.textContent = message;
    caption.setAttribute("data-tone", tone);
  }

  function emit(): void {
    render();
    pulse();
    options.onChange?.(draft.minorUnits, currency);
  }

  function pressDigit(digit: string): void {
    if (draft.appendDigit(digit)) {
      setCaption("", "muted");
      emit();
      return;
    }
    // The only reachable rejection for a real digit is the representation
    // bound, so the message names that rather than guessing at a policy.
    setCaption("That is the largest amount this field can hold.", "danger");
  }

  // ---- Currency chooser ---------------------------------------------------
  //
  // Changing currency **clears the amount**. Nura does not convert, holds no
  // rate, and must never imply that it does: carrying 24500 minor units from
  // EUR to JPY would silently redenominate the value by two orders of
  // magnitude and present the result as if a conversion had happened. Clearing
  // is the honest behaviour, it costs a few taps, and it is stated out loud
  // rather than left to be inferred from a number that suddenly changed.
  const currencyOptions = options.currencies.map((code) =>
    el("button", {
      class: "nura-currency__option",
      text: code,
      attrs: { type: "button", role: "radio", "aria-checked": code === currency },
      data: { currency: code },
    }),
  );

  const currencyGroup = el(
    "div",
    {
      class: "nura-currency",
      attrs: { role: "radiogroup", "aria-label": "Currency" },
    },
    currencyOptions,
  );

  function selectCurrency(code: string): void {
    if (code === currency || !options.currencies.includes(code)) return;
    const hadAmount = !draft.isEmpty;
    draft.clear();
    currency = code;
    for (const node of currencyOptions) {
      node.setAttribute("aria-checked", String(node.getAttribute("data-currency") === code));
    }
    if (hadAmount) {
      setCaption(`Amount cleared. Enter it again in ${code} — Nura does not convert.`, "attention");
    } else {
      setCaption("", "muted");
    }
    emit();
  }

  currencyGroup.addEventListener("click", (event) => {
    const target = (event.target as Element | null)?.closest(".nura-currency__option");
    if (!(target instanceof HTMLButtonElement)) return;
    const code = target.getAttribute("data-currency");
    if (code !== null) selectCurrency(code);
  });

  // ---- Keypad -------------------------------------------------------------
  const keypad = el(
    "div",
    { class: "nura-keypad", attrs: { role: "group", "aria-label": "Amount keypad" } },
    [
      ...KEYPAD_DIGITS.map((digit) =>
        el("button", {
          class: "nura-keypad__key",
          text: digit,
          attrs: { type: "button" },
          data: { key: digit },
        }),
      ),
      el("button", {
        class: "nura-keypad__key",
        text: "C",
        attrs: { type: "button", "aria-label": "Clear amount" },
        data: { key: "clear", role: "action" },
      }),
      el("button", {
        class: "nura-keypad__key",
        text: "0",
        attrs: { type: "button" },
        data: { key: "0" },
      }),
      el("button", {
        class: "nura-keypad__key",
        staticHtml: icon("backspace", 22),
        attrs: { type: "button", "aria-label": "Delete last digit" },
        data: { key: "delete", role: "action" },
      }),
    ],
  );

  keypad.addEventListener("click", (event) => {
    const target = (event.target as Element | null)?.closest(".nura-keypad__key");
    if (!(target instanceof HTMLButtonElement)) return;
    const key = target.getAttribute("data-key");
    if (key === null) return;
    if (key === "clear") {
      draft.clear();
      setCaption("", "muted");
      emit();
      return;
    }
    if (key === "delete") {
      draft.deleteLast();
      setCaption("", "muted");
      emit();
      return;
    }
    pressDigit(key);
  });

  function isTextEntry(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    return (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target.isContentEditable
    );
  }

  function onKeyDown(event: KeyboardEvent): void {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (isTextEntry(event.target)) return;

    if (event.key >= "0" && event.key <= "9" && event.key.length === 1) {
      event.preventDefault();
      pressDigit(event.key);
      return;
    }
    if (event.key === "Backspace") {
      event.preventDefault();
      draft.deleteLast();
      setCaption("", "muted");
      emit();
      return;
    }
    if (event.key === "Escape") {
      if (draft.clear()) {
        setCaption("", "muted");
        emit();
      }
    }
  }

  window.addEventListener("keydown", onKeyDown);

  const root = el("div", { class: "nura-composer" }, [
    el("div", { class: "nura-composer__display" }, [display]),
    el("div", { class: "nura-composer__deck" }, [
      options.currencies.length > 1 ? currencyGroup : undefined,
      keypad,
      options.action === undefined
        ? undefined
        : el("div", { class: "nura-composer__action" }, [options.action]),
    ]),
  ]);
  render();

  return {
    root,
    get minorUnits(): bigint {
      return draft.minorUnits;
    },
    get currency(): string {
      return currency;
    },
    setCurrency(code: string): void {
      selectCurrency(code);
    },
    destroy(): void {
      window.removeEventListener("keydown", onKeyDown);
    },
  };
}
